import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { loadRevnuPost, jsonLdScript } from '../utils/revnuBlog';
import './RevnuBlogPost.css';

// A post written and published in Revnu, rendered with the same design as the
// legacy markdown posts (BlogPost.js). The data is the key-free JSON from
// scripts/revnu-sync.js; the prerender bakes this page into static HTML.

export const BlogArticleLoading = () => (
  <div className="min-h-screen bg-white flex items-center justify-center">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-10 w-10 border-3 border-gray-200 border-t-[#0065F8] mb-4"></div>
      <p className="text-gray-500 text-sm">Loading article...</p>
    </div>
  </div>
);

const formatDate = (isoDay) =>
  // +T00:00:00 forces local-time parsing — see BlogList
  new Date(isoDay + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

// Head straight from Revnu: headMeta (canonical and og:url = canonicalUrl)
// plus every jsonLd entry, nothing added.
export const RevnuHead = ({ head }) => (
  <Helmet>
    <title>{head.title}</title>
    <link rel="canonical" href={head.canonical} />
    {head.meta.map((m, i) => <meta key={i} {...m} />)}
    {head.jsonLd.map((entry, i) => (
      <script key={i} type="application/ld+json">{jsonLdScript(entry)}</script>
    ))}
  </Helmet>
);

const RevnuBlogPost = ({ entry }) => {
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);
  const [readingProgress, setReadingProgress] = useState(0);

  useEffect(() => {
    let alive = true;
    setPost(null);
    setError(null);
    loadRevnuPost(entry.slug)
      .then((data) => { if (alive) setPost(data); })
      .catch((err) => { if (alive) setError(err.message); });
    return () => { alive = false; };
  }, [entry.slug]);

  useEffect(() => {
    const handleScroll = () => {
      const progress = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      setReadingProgress(Math.min(progress, 100));
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="container mx-auto px-6 pb-12 pt-28 sm:pt-32 max-w-3xl">
          <div className="bg-red-50 border border-red-200 rounded-lg p-12 text-center">
            <h1 className="text-3xl font-bold text-red-600 mb-4">Article Not Found</h1>
            <p className="text-gray-600 mb-8">{error}</p>
            <Link
              to="/blog"
              className="inline-flex items-center px-6 py-3 bg-[#0065F8] hover:bg-[#0055D6] text-white rounded-full transition-colors font-bold"
              style={{ fontFamily: "'Fredoka', sans-serif", letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 14, boxShadow: "0px 4px 0px #073B8E" }}
            >
              ← Back to Blog
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!post) return <BlogArticleLoading />;

  return (
    <div className="min-h-screen bg-white" style={{fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'}}>
      <RevnuHead head={post.head} />
      {/* "All articles" bar + reading progress — same as BlogPost */}
      <nav className="sticky bg-white/95 backdrop-blur-sm border-b border-gray-200 z-30 mt-24 sm:mt-28" style={{ top: 92 }}>
        <div className="container mx-auto px-6 py-4 max-w-3xl">
          <Link
            to="/blog"
            className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors font-medium text-sm"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            All Articles
          </Link>
        </div>
        <div className="absolute left-0 right-0 bottom-0 h-1 bg-gray-100">
          <div
            className="h-full bg-[#0065F8] transition-all duration-150"
            style={{ width: `${readingProgress}%` }}
          ></div>
        </div>
      </nav>

      <article className="container mx-auto px-6 py-12 md:py-16 max-w-3xl" data-revnu-post={post.slug}>
        <header className="mb-12 pb-12 border-b border-gray-200">
          {post.heroImageUrl && (
            <div className="mb-8 -mx-6 md:-mx-0 rounded-none md:rounded-lg overflow-hidden">
              <img
                src={post.heroImageUrl}
                alt={post.title}
                className="w-full h-auto object-cover"
              />
            </div>
          )}
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-[1.1] tracking-tight">
              {post.title}
            </h1>
            {post.description && (
              <p className="text-xl md:text-2xl text-gray-600 leading-relaxed">
                {post.description}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <img
                src="/assets/pixley-panda-nobg.png"
                alt="Pixley"
                className="w-6 h-6 rounded-full object-cover"
              />
              <span className="font-medium text-gray-900">{post.author}</span>
            </div>
            {post.date && (
              <>
                <span className="text-gray-400">·</span>
                <time dateTime={post.date}>{formatDate(post.date)}</time>
              </>
            )}
            {post.updated && post.updated !== post.date && (
              <>
                <span className="text-gray-400">·</span>
                <span className="text-gray-600">Updated {formatDate(post.updated)}</span>
              </>
            )}
            <span className="text-gray-400">·</span>
            <span>{post.readingTime} min read</span>
          </div>
        </header>

        {/* Revnu's escaped, render-ready HTML, styled to match the markdown posts */}
        <div className="revnu-body" dangerouslySetInnerHTML={{ __html: post.html }} />

        {post.faqItems.length > 0 && (
          <section className="mt-14" aria-labelledby="post-faq">
            <h2 id="post-faq" className="text-2xl font-bold mb-6" style={{ fontFamily: "'Fredoka', sans-serif", color: '#0B1A33' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-5">
              {post.faqItems.map((f, i) => (
                <div key={i} className="rounded-2xl border border-gray-200 p-5" style={{ background: '#F8FBFF' }}>
                  <h3 className="font-bold mb-2" style={{ fontFamily: "'Fredoka', sans-serif", color: '#0B1A33', fontSize: 17 }}>{f.question}</h3>
                  <p className="text-gray-700 leading-relaxed" style={{ fontSize: 15 }}>{f.answer}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {post.citations.length > 0 && (
          <section className="mt-14" aria-labelledby="post-sources">
            <h2 id="post-sources" className="text-2xl font-bold mb-6" style={{ fontFamily: "'Fredoka', sans-serif", color: '#0B1A33' }}>
              Sources
            </h2>
            <ol className="space-y-2 ml-6 list-decimal marker:text-gray-400">
              {post.citations.map((url) => (
                <li key={url} className="text-gray-700 text-sm pl-2 break-words">
                  <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: '#0065F8' }}>{url}</a>
                </li>
              ))}
            </ol>
          </section>
        )}

        {post.related.length > 0 && (
          <section className="mt-14" aria-labelledby="post-related">
            <h2 id="post-related" className="text-2xl font-bold mb-6" style={{ fontFamily: "'Fredoka', sans-serif", color: '#0B1A33' }}>
              Keep Reading
            </h2>
            <ul className="space-y-3">
              {post.related.map((p) => (
                <li key={p.href}>
                  <Link to={p.href} className="font-semibold hover:underline" style={{ color: '#0065F8' }}>
                    {p.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/blog" className="font-semibold hover:underline" style={{ color: '#0065F8' }}>
                  All parenting & screen-time guides →
                </Link>
              </li>
            </ul>
          </section>
        )}

        <div className="mt-16 pt-12 border-t border-gray-200 flex items-center justify-between">
          <Link
            to="/blog"
            className="inline-flex items-center transition-all duration-150"
            style={{
              fontFamily: "'Fredoka', sans-serif",
              background: "linear-gradient(180deg, #0A7DFF 0%, #0065F8 100%)",
              color: "#FFFFFF",
              fontWeight: 800,
              fontSize: 14,
              padding: "12px 24px",
              borderRadius: 999,
              border: "none",
              boxShadow: "0px 4px 0px #073B8E",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            ← Back to All Articles
          </Link>
          <a
            href="https://pixleyai.com"
            className="inline-flex items-center gap-2 transition-all duration-150"
            style={{
              fontFamily: "'Fredoka', sans-serif",
              background: "#FFFFFF",
              border: "2px solid rgba(0,101,248,0.18)",
              color: "#0065F8",
              fontWeight: 800,
              fontSize: 13,
              padding: "10px 18px",
              borderRadius: 999,
              boxShadow: "0px 3px 0px rgba(11,26,51,0.06)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            <img src="/assets/pixley-panda-nobg.png" alt="Pixley" className="w-5 h-5" />
            Try Pixley Free
          </a>
        </div>
      </article>
    </div>
  );
};

export default RevnuBlogPost;
